import { Constants, Location } from './common';
import { Developer } from './developers';
import { Education, UserProject } from './experience';
import { Position } from './position';
import {
  AdditionalLanguage,
  ApplicationState,
  Availability,
  Profile,
  Role,
  Skill,
  Specialization,
} from './profile';
import { Project, ProjectsListType } from './projects';
import { Tag } from './tags';

// ------------ Common API ------------ //

export type GetConstansResponse = Constants;

export type GetLocationResponse = Location;

type UploadCvType = 'profile' | 'developer';

export interface UploadCvRequest {
  cv: FormData;
  type: UploadCvType;
}

export interface UploadCvResponse {
  filename: string;
  url: string;
  id: string;
}

export interface UploadCvSourceDataResponse {
  attachmentId: string;
  sourceDataId: string;
}

export interface UploadLinkedinSourceDataResponse {
  sourceDataId: string;
  linkedinId: string;
}

export interface DeleteCvRequest {
  type: UploadCvType;
}

export type DeleteCvResponse = boolean;

// ------------ Common API ------------ //

// ------------ Profile API ------------ //

export type GetProfileResponse = Profile;

export type GetApplicationStateResponse = ApplicationState;

export type GetSkillsResponse = Array<Skill>;

export type GetSpecializationsResponse = Array<Specialization>;

export type UpdateProfileRequest = Partial<Profile>;

export type UpdateAvailabilityRequest = Partial<Availability>;

export interface UpdateSkillsRequest {
  skills: Array<Omit<Skill, 'id'> & { id?: string }>;
}

export interface UpdateSpecializationsRequest {
  specializations: Specialization[];
  experience_since?: string;
  seniority?: string;
}

export interface UpdateAvatarRequest {
  photo: FormData;
}

export type GetLanguagesResponse = Array<AdditionalLanguage>;

export type UpdateLanguagesRequest = {
  languages: Array<Omit<AdditionalLanguage, 'id'> & { id?: string }>;
};

export enum EnrichmentJobStatus {
  Running = 'Running',
  Error = 'Error',
  Finished = 'Finished',
}

export enum EnrichmentJobStage {
  CvParse = 'CvParse',
  ProfileEnrichment = 'ProfileEnrichment',
}

export interface EnrichmentJob {
  id: string;
  contactId: string;
  status: EnrichmentJobStatus;
  attachmentId?: string;
  traceId?: string;
  stage?: EnrichmentJobStage;
  created_date?: string;
  update_date?: string;
}

export enum EnrichmentStatus {
  Processing = 'Processing',
  Finished = 'Finished',
  FinishedWithErrors = 'FinishedWithErrors',
}

export interface EnrichmentJobRef {
  id: string;
  status?: string;
}

export interface EnrichmentResult {
  status: EnrichmentStatus;
  profileEnrichmentJob: EnrichmentJobRef;
  scamCheckJob?: EnrichmentJobRef;
}

export interface ParseCVResponse {
  id: string;
  filename: string;
  url: string;
  enrichmentJob: EnrichmentJob;
}

export enum AttachmentType {
  CV = 'CV',
  Other = 'Other',
}

export interface Attachment {
  id: string;
  contactId: string;
  name: string;
  filename: string;
  date: string;
  isUpload: boolean;
  url: string;
  type: AttachmentType;
}

export type GetAttachmentsResponse = Attachment[];

// ------------ Profile API ------------ //

// ------------ Experience API ------------ //

export interface GetUserProjectRequest {
  id: string;
}

export type GetUserProjectResponse = UserProject;

export type GetUserProjectsResponse = Array<UserProject>;

export type AddProjectRequest = Partial<UserProject>;

export type UpdateProjectRequest = Partial<UserProject> & { id: string };

export interface DeleteProjectRequest {
  id: string;
}

export type DeleteProjectResponse = void;

export interface GetEducationRequest {
  id: string;
}

export type GetEducationResponse = Education;

export type GetEducationsResponse = Array<Education>;

export type AddEducationRequest = Partial<Education>;

export type UpdateEducationRequest = Partial<Education> & { id: string };

export interface DeleteEducationRequest {
  id: string;
}

// ------------ Experience API ------------ //

// ------------ Tags API ------------ //

export type GetTagsResponse = Array<Tag>;

export interface GetProjectTagsRequest {
  projectId: string;
}

export type GetProjectTagsResponse = Array<Tag>;

export interface GetContactProjectTagsRequest {
  contactKey: string;
}

export type GetContactProjectTagsResponse = {
  projectTags: Record<string, Array<Tag>>;
};

export interface GetHubspotProjectTagsRequest {
  hubspotProjectId: string;
}

export type GetHubpostProjectTagsResponse = Array<Tag>;

export interface UpdateProjectTagsRequest {
  tagIds: Array<string>;
  projectId: string;
}

export type UpdateProjectTagsResponse = void;

// ------------ Tags API ------------ //

// ------------ SignUp API ------------ //

export enum RegistrationSource {
  RegistrationForm = 'RegistrationForm',
  GoogleRegistrationForm = 'GoogleRegistrationForm',
}

export interface SignUpPayload {
  firstName: string;
  lastName: string;
  email: string;
  country: string;
  phone?: string;
  utm_source?: string;
  utm_content?: string;
  utm_term?: string;
  utm_campaign?: string;
  utm_id?: string;
  utm_medium?: string;
  site_ref?: string;
}

export type SignUpResponse =
  | {
      success: false;
      error: string;
    }
  | {
      success: true;
      qualified: boolean;
      token: string;
      contact: {
        apiKey: string;
        disqualification_reasons?: Array<string>;
      };
    };

export interface GoogleAuthPayload {
  googleAuthToken: string;
  country?: string;
  utm_source?: string;
  utm_content?: string;
  utm_term?: string;
  utm_campaign?: string;
  utm_id?: string;
  utm_medium?: string;
  site_ref?: string;
}

export type GoogleAuthResponse =
  | {
      success: false;
      error: string;
    }
  | {
      success: true;
      mode: 'login';
      ssoDomainToken: string;
    }
  | {
      success: true;
      mode: 'signup';
      token: string;
      apiKey: string;
      qualified?: boolean;
    };

// ------------ SignUp API ------------ //

// ------------ Developers API ------------ //

export interface GetDevelopersResponse {
  partnerDevelopers: Array<Developer>;
  total: number;
}
export interface UpdateDeveloperDto {
  id?: string;
  firstName: string;
  lastName: string;
  email?: string | null;
  specialization_seniority: string | null;
  main_specialization:
    | string
    | { id: string; title: string; seniority: string | null };
  max_rate_per_hour: number;
  available_hours_per_week: string | null;
  experience_since: string | null;
  en_spoken: string | null;
  link_to_cv: string;
  country: string;
  mainSkills: Array<string>;
}

export interface UpdateDevelopersRequest {
  partnerDevelopers: Array<UpdateDeveloperDto>;
}

export interface ValidationError {
  property: string;
  value: string;
  constraints: Record<string, string>;
  target: UpdateDeveloperDto;
}

export interface UpdateDevelopersResponse {
  partnerDevelopers: Array<Developer>;
  validationErrors: {
    updateErrors: Record<string, Array<ValidationError>>;
    creationErrors: Array<ValidationError>;
  };
}

// ------------ Developers API ------------ //

// ------------ Projects API ------------ //

export interface GetProjectsRequest {
  type: ProjectsListType;
  page: number;
  perPage: number;
  role: Role;
}

export type GetAllProjectsRequest = Omit<
  GetProjectsRequest,
  'page' | 'perPage'
>;

export interface GetProjectsResponse {
  projects: Project[];
  total: number;
}

export interface GetProjectRequest {
  dealId: string;
  /**
   * If `role !== 'partner manager'` - then developerId will be cutted off
   */
  developerId?: string;
  role?: Role;
}

export type GetProjectResponse = Project;

interface ApplyProjectAnswer {
  hubspotProjectQuestionId: string;
  text: string;
}
export interface ApplyToProjectRequest {
  hubspotProjectId: string;
  contactId: string;
  desiredRateMin?: number;
  desiredRateMax?: number;
  candidateRateComment?: string;
  candidateApplicationComment?: string;
  readyToStartAt: string;
  vacationPlans: string;
  interviewTimeslots: string;
  workingHoursFrom: string;
  workingHoursTo: string;
  timeZone: string;
  projectAnswers?: Array<ApplyProjectAnswer>;
}

export type ApplyToProjectResponse = Project;

export interface DeclineProjectRequest {
  hubspotProjectId: string;
  contactId: string;
  declineComment?: string;
}

export type DeclineProjectResponse = Project;

// ------------ Projects API ------------ //

// ------------ Positions API ------------ //

export type GetAssignedPositionsResponse = Position[];

// ------------ Positions API ------------ //
